/**
 * useSavedCards / useRevokeSavedCard / useChargeCardSummary
 * ----------------------------------------------------------------------------
 * Hooks de cartões tokenizados (Fase 5):
 *   - useSavedCards(customerId)         : lista cartões ativos do cliente
 *   - useRevokeSavedCard()              : revogação (soft-delete) com confirmação
 *   - useChargeCardSummary(cobrancaId)  : cartão usado na última tentativa OK
 *
 * Todas as chamadas vão pelas novas RPCs com p_tenant_hint=NULL (o backend
 * resolve via get_validated_tenant_id).
 *
 * RELEASE: 2026-06-20 v1.0.0 - Versão inicial.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useSavedCards');

/** Resumo de assinatura que usa o cartão como default. */
export interface SavedCardSubscriptionRef {
  subscription_id: string;
  descricao: string | null;
  status: string;
  dunning_state: string;
}

/** Cartão tokenizado tal como retornado pela RPC. */
export interface SavedCard {
  id: string;
  brand: string | null;
  last4: string;
  exp_month: number;
  exp_year: number;
  holder_name_masked: string | null;
  provider_slug: string;
  failure_count: number;
  last_success_at: string | null;
  last_failed_at: string | null;
  created_at: string;
  default_of_subscriptions: SavedCardSubscriptionRef[];
}

export function useSavedCards(customerId?: string, enabled = true) {
  return useQuery({
    queryKey: ['saved-cards', customerId],
    enabled: !!customerId && enabled,
    queryFn: async (): Promise<SavedCard[]> => {
      const { data, error } = await supabase.rpc('get_saved_cards_by_customer', {
        p_crm_customer_id: customerId!,
        p_tenant_hint: null,
      });
      if (error) throw error;
      return ((data ?? []) as unknown) as SavedCard[];
    },
  });
}

export function useRevokeSavedCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { cardId: string; reason?: string; customerId?: string }) => {
      const { data, error } = await supabase.rpc('revoke_saved_card', {
        p_card_id: params.cardId,
        p_reason: params.reason ?? null,
        p_tenant_hint: null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: (_id, vars) => {
      toast.success('Cartão revogado com sucesso');
      qc.invalidateQueries({ queryKey: ['saved-cards', vars.customerId] });
      qc.invalidateQueries({ queryKey: ['saved-cards'] });
    },
    onError: (err: any) => {
      logger.error('revoke_saved_card falhou', { err: err?.message });
      toast.error(err?.message || 'Falha ao revogar cartão');
    },
  });
}

/** Resumo do cartão usado na última payment_attempt bem-sucedida da cobrança. */
export interface ChargeCardSummary {
  attempt_id: string;
  card_token_id: string;
  brand: string | null;
  last4: string;
  exp_month: number;
  exp_year: number;
  holder_name_masked: string | null;
  provider_slug: string;
  provider_charge_id: string | null;
  attempt_status: string;
  three_ds_used: boolean;
  created_at: string;
}

export function useChargeCardSummary(cobrancaId?: string, enabled = true) {
  return useQuery({
    queryKey: ['charge-card-summary', cobrancaId],
    enabled: !!cobrancaId && enabled,
    queryFn: async (): Promise<ChargeCardSummary | null> => {
      const { data, error } = await supabase.rpc('get_charge_card_summary', {
        p_cobranca_id: cobrancaId!,
        p_tenant_hint: null,
      });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      return (row ?? null) as ChargeCardSummary | null;
    },
  });
}
